import { LedgerAccountsDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  ParseAccountingAccountsImportDocument,
  ParseAccountingAccountsImportMutation,
  StructuredError,
  SubmitAccountingAccountsImportDocument,
  SubmitAccountingAccountsImportMutation,
} from 'generated/resources/graphql'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'

const extractParseData = (data: ParseAccountingAccountsImportMutation) =>
  data.financeMutations.parseFinanceAccountingAccountImport

const extractSubmitData = (data: SubmitAccountingAccountsImportMutation) =>
  data.financeMutations.importFinanceAccountingAccountImport

export const useAccountsImport = () => {
  const [parseAccountsImportFile, { loading: parsing, data: parseData, error: parseError }] =
    useMutation(ParseAccountingAccountsImportDocument, { extractorFn: extractParseData })

  const [submitAccountsImportFile, { loading: submitting, data: submitData, error: submitError }] =
    useMutation(SubmitAccountingAccountsImportDocument, {
      refetchQueries: [LedgerAccountsDataTableDocument],
      extractorFn: extractSubmitData,
      update(cache, { data }) {
        const legalEntity =
          data?.financeMutations?.importFinanceAccountingAccountImport?.accountImport?.legalEntity

        if (legalEntity) {
          cache.evict({
            id: cache.identify(legalEntity),
            fieldName: 'financeAccounts',
          })
          cache.evict({
            id: cache.identify(legalEntity),
            fieldName: 'financeAccountsConnection',
          })
        }
      },
    })

  const parseSummary = parseData && extractParseData(parseData)?.accountImport
  const internalErrors = parseError || submitError

  const validateParseErrors = useUnsafeMemo(() => {
    const errors = parseData && extractParseData(parseData)?.errors

    if (!errors || errors.length === 0) return

    const structuredErrors: StructuredError[] = errors.filter(
      (error): error is StructuredError => error.__typename === 'StructuredError'
    )

    if (structuredErrors[0]?.field === 'errors') return structuredErrors[0].messages[0]

    return structuredErrors.flatMap((error) => error.messages)
  }, [parseData])

  const validateSubmitErrors = useUnsafeMemo(() => {
    const errors = submitData && extractSubmitData(submitData)?.errors

    if (!errors || errors.length === 0) return

    const structuredErrors: StructuredError[] = errors.filter(
      (error): error is StructuredError => error.__typename === 'StructuredError'
    )

    if (structuredErrors[0]?.field === 'errors') return structuredErrors[0].messages[0]

    return structuredErrors.flatMap((error) => error.messages)
  }, [submitData])

  return {
    parsing,
    submitting,
    internalErrors,
    validateParseErrors,
    validateSubmitErrors,
    parseSummary: !validateParseErrors && parseSummary ? parseSummary : undefined,
    parseImportFile: (legalEntityId: number, file: File) =>
      parseAccountsImportFile({ variables: { file, legalEntityId } }),
    submitImportFile: (legalEntityId: number, file: File) =>
      submitAccountsImportFile({ variables: { file, legalEntityId } }),
  }
}
